import {match} from 'ts-pattern';

import {isArray, isNotNil} from 'ramda-adjunct';

import {AddBasketItemBody} from '@dms/api/warehouse';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {ArticleUpdateJobMaterialModalListWarehouseTabExternalFilters} from './ArticleUpdateJobMaterialModalListWarehouseTabExternalFilters';

interface ArticleUpdateJobMaterialModalListWarehouseTabProps extends RequiredTestIdProps {
  onAddItemsToBasket: (items: AddBasketItemBody) => Promise<void>;
}

type ArticleUpdateJobItemRowData = {
  catalogNumber: {value: string};
  manufacturerId: {value: {key: string}};
};

export function ArticleUpdateJobMaterialModalListWarehouseTab(
  props: ArticleUpdateJobMaterialModalListWarehouseTabProps
) {
  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder();

  const actionCallback: ActionCallback = async ({actionKey, rowData}) => {
    await match(actionKey)
      .with('add', async () => {
        const items = (isArray(rowData) ? rowData : [rowData]) as ArticleUpdateJobItemRowData[];

        const validItems = items.filter(
          (item): item is ArticleUpdateJobItemRowData =>
            isNotNil(item?.catalogNumber?.value) && isNotNil(item?.manufacturerId?.value?.key)
        );

        if (validItems.length === 0) {
          return logError('Selected items are missing required data');
        }

        const itemsPayload = items.reduce(
          (acc, item) => ({
            catalogNumber: acc.catalogNumber.concat(item?.catalogNumber?.value),
            manufacturerId: acc.manufacturerId.concat(item?.manufacturerId?.value?.key),
          }),
          {catalogNumber: [], manufacturerId: []} as AddBasketItemBody
        );

        await props.onAddItemsToBasket(itemsPayload);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };
  return (
    <FullscreenDataGridWrapper
      hideCardWrapper
      externalFilterId={ARTICLE_UPDATE_JOB_MATERIAL_EXTERNAL_FILTER_ID}
      folderTreeRef={folderTreeRef}
      data-testid={props['data-testid']}
    >
      <DmsDataGrid
        // DG must be re-rendered on folder change to update the query modifier
        // eslint-disable-next-line no-restricted-syntax
        key={dataGridModifier?.key}
        gridCode={WAREHOUSE_GRID_CODES.articleUpdateJobBasketItems}
        actionCallback={actionCallback}
        externalFilterId={ARTICLE_UPDATE_JOB_MATERIAL_EXTERNAL_FILTER_ID}
        filterComponent={(datagridProps) => (
          <ArticleUpdateJobMaterialModalListWarehouseTabExternalFilters {...datagridProps} />
        )}
        queryModifier={dataGridModifier?.queryModifier}
        data-testid={WAREHOUSE_GRID_CODES.articleUpdateJobBasketItems}
      />
    </FullscreenDataGridWrapper>
  );
}

const ARTICLE_UPDATE_JOB_MATERIAL_EXTERNAL_FILTER_ID = 'articleUpdateJob-material-external-filter';
